/**
 * Paywall — visas när användaren godkänner borttagning utan aktiv plan.
 * Scan är gratis; borttagning kräver Skydd/Familj.
 */
import { useState, type CSSProperties } from "react";
import { startBillingCheckout, loadSession } from "../lib/api";

const NAVY = "#0b1f4a";
const GOLD = "#b68235";
const GOLD_BG = "#fff3e4";
const MUTED = "#7d7979";

export type PaywallPlanId = "skydd_month" | "skydd_year" | "familj_month";

const PLANS: {
  id: PaywallPlanId;
  title: string;
  price: string;
  blurb: string;
  primary?: boolean;
}[] = [
  {
    id: "skydd_month",
    title: "Skydd",
    price: "29 kr/mån",
    blurb: "Borttagning + bevakning för en person",
    primary: true,
  },
  {
    id: "skydd_year",
    title: "Skydd årsplan",
    price: "249 kr/år",
    blurb: "Samma skydd, lägre månadspris",
  },
  {
    id: "familj_month",
    title: "Familj",
    price: "49 kr/mån",
    blurb: "Upp till 4 personer",
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
  /** Optional: how many removals they tried to approve */
  pendingCount?: number;
};

export default function PaywallModal({ open, onClose, pendingCount }: Props) {
  const [busy, setBusy] = useState<PaywallPlanId | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const pay = async (planId: PaywallPlanId) => {
    setBusy(planId);
    setError(null);
    try {
      const email = loadSession()?.user?.email;
      const res = await startBillingCheckout(planId, email || undefined);
      const url = res.data?.approveUrl;
      if (!url) throw new Error("Ingen betalningslänk");
      window.location.href = url;
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Kunde inte starta betalning. Försök igen eller öppna Priser."
      );
      setBusy(null);
    }
  };

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "color-mix(in srgb, #0b1f4a 45%, transparent)",
    zIndex: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  };

  const card: CSSProperties = {
    background: "#fff",
    borderRadius: 10,
    maxWidth: 420,
    width: "100%",
    padding: "28px 26px 22px",
    boxShadow: "0 24px 64px color-mix(in srgb, #2d2b2b 28%, transparent)",
    fontFamily: '"Lora", Georgia, serif',
    color: "#201f1d",
  };

  const planBtn = (primary?: boolean): CSSProperties => ({
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "14px 16px",
    borderRadius: 6,
    border: primary ? `1.5px solid ${GOLD}` : "1px solid #d7d3d3",
    background: primary ? GOLD_BG : "#fff",
    cursor: busy ? "wait" : "pointer",
    fontFamily: "inherit",
    marginBottom: 10,
    opacity: busy ? 0.75 : 1,
  });

  return (
    <div
      style={overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div style={card}>
        <div
          style={{
            fontSize: 11.5,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: GOLD,
            marginBottom: 8,
          }}
        >
          Gå vidare till betalning
        </div>
        <h2
          id="paywall-title"
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 400,
            fontSize: 26,
            margin: "0 0 10px",
            color: NAVY,
          }}
        >
          Aktivera Skydd för att ta bort
        </h2>
        <p style={{ fontSize: 14.5, lineHeight: 1.55, color: MUTED, margin: "0 0 18px" }}>
          Scanen är gratis. Borttagning
          {pendingCount && pendingCount > 1 ? ` av ${pendingCount} källor` : ""} ingår i Skydd och
          Familj. Välj plan — du skickas till PayPal för att slutföra.
        </p>

        {PLANS.map((p) => (
          <button
            key={p.id}
            type="button"
            style={planBtn(p.primary)}
            disabled={!!busy}
            onClick={() => void pay(p.id)}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <strong style={{ color: NAVY, fontSize: 15 }}>{p.title}</strong>
              <span style={{ color: GOLD, fontWeight: 600, fontFeatureSettings: '"tnum" 1' }}>
                {busy === p.id ? "Öppnar…" : p.price}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: MUTED, marginTop: 4 }}>{p.blurb}</div>
          </button>
        ))}

        {error && (
          <p style={{ color: "#B42318", fontSize: 13, margin: "4px 0 10px" }}>{error}</p>
        )}

        <div
          style={{
            display: "flex",
            gap: 12,
            marginTop: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={!!busy}
            style={{
              padding: "10px 16px",
              border: "1px solid #d7d3d3",
              borderRadius: 4,
              background: "transparent",
              fontSize: 13.5,
              color: MUTED,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Inte nu
          </button>
          <a
            href="https://gdpr-nordic.org/priser"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: "#1a3a7a" }}
          >
            Jämför priser
          </a>
        </div>
      </div>
    </div>
  );
}
